import { useHistory } from 'react-router';

import { getRedirectPath } from '@flow/libs/workspace/folders';
import useWindowSize from '@app/hooks/use-window-size';
import { withWorkspaceNamespace } from '@app/hocs/with-current-workspace';
import useClientFeatureFlag from '@app/hooks/use-client-feature-flag';

import { checkAcl } from '@flow/libs/utils/acl';
import SetContractName from '@app/components/document-topbar/left-side/set-contract-name';
import MoveContract from '@app/components/modals/move-contract';
import ContractValue from '@app/components/contract-value';
import Tags from '@app/components/tags';
import State from '@app/components/document-information/state';
import TopbarWorkspace from '@app/components/document-topbar/left-side/topbar-workspace';
import { FolderBreadCrumb } from '@app/components/bread-crumb/folder';
import { mapToLink } from '@app/components/bread-crumb/helper';
import { DOCUMENT_COLLAPSED_LAYOUT_SIZE } from '@app/components/document-layout-container/helpers';
import style from './left-side.module.scss';

type Props = {
  agreement: Oneflow.Document;
};

type Breakpoints = Array<{
  maxTagsNumber: number;
  query: {
    minWidth?: number;
    maxWidth?: number;
  };
}>;

const breakpointLg = 600;
const breakpointXl = 1920;

const breakpoints: Breakpoints = [
  {
    maxTagsNumber: 0,
    query: {
      maxWidth: breakpointLg - 1,
    },
  },
  {
    maxTagsNumber: 2,
    query: {
      minWidth: breakpointLg,
      maxWidth: breakpointXl,
    },
  },
  {
    maxTagsNumber: 8,
    query: {
      minWidth: breakpointXl + 1,
    },
  },
];

const getLinks = (
  workspace: Oneflow.Workspace,
  folders?: Oneflow.Document['folder']['resolvedPath'],
) => {
  let links = [];

  links.push(mapToLink(workspace));

  if (folders) {
    links = [
      ...links,
      ...folders.map(mapToLink),
    ];
  }

  return links;
};

const LeftSide = ({ agreement }: Props) => {
  const windowSize = useWindowSize();
  const history = useHistory();

  const isDeviceSmallerThanLg = windowSize.width && windowSize.width < breakpointLg;
  const isCollapsedLayout =
    windowSize.width && windowSize.width < parseInt(DOCUMENT_COLLAPSED_LAYOUT_SIZE, 10);
  const links = getLinks(
    {
      ...agreement.collection,
      id: -1,
    },
    agreement.folder?.resolvedPath,
  );
  const isFolderV2 = useClientFeatureFlag('folder-v2');

  const renderTopBarWorkspace = (onClick: () => void) => {
    if (isDeviceSmallerThanLg) {
      return null;
    }

    return (
      <TopbarWorkspace
        agreement={agreement}
        onClick={onClick}
      />
    );
  };

  const renderTags = () => {
    if (isCollapsedLayout) {
      return null;
    }

    return (
      <Tags
        allowSearch
        searchGlobally
        allowTagChange={checkAcl(agreement.acl, 'agreement:tag:use')}
        breakpoints={breakpoints}
        responsive
        targetId={agreement.id}
        targetType="agreement"
        amplitudeScope="contract view"
        renderAsNativeAnchorElement
        isDocumentPage
      />
    );
  };

  return (
    <div className={style.DocumentInformation}>
      <div className={style.NameTagContainer}>
        <SetContractName agreement={agreement} />
        {renderTags()}
      </div>
      <div className={style.DocumentInformationContainer}>
        <State agreement={agreement} />
        {!isDeviceSmallerThanLg && isFolderV2 && (
          <div className={style.FolderBreadCrumbContainer}>
            <FolderBreadCrumb
              customBreadCrumbDropdownClassName={style.BreadCrumbDropdown}
              showLastBreadCrumbLink
              links={links}
              onBreadCrumbItemClick={(id) => {
                if (id) {
                  history.push({
                    pathname: withWorkspaceNamespace(agreement.collection)('/contracts/all'),
                    search: getRedirectPath(location, id, true),
                  });
                }
              }}
            />
          </div>
        )}
        {!isFolderV2 && (
          <MoveContract agreement={agreement}>
            {(onClick: () => void) => renderTopBarWorkspace(onClick)}
          </MoveContract>
        )}
        <ContractValue
          agreement={agreement}
          agreementValue={agreement.agreementValue}
          className={style.ContractValue}
        />
      </div>
    </div>
  );
};

export default LeftSide;
